import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BarLoader } from 'react-spinners';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import './App.css';

import { LinkData } from './types/post.types';
import { isMobile } from './utils/helpers';
import Header from './components/Header';
import PostInfo from './components/PostInfo';
import ShareButtons from './components/ShareButtons';
import StoreLinks from './components/StoreLinks';

const App: React.FC = () => {
    const { postId, lang } = useParams<{ postId: string; lang: string }>();
    const [link, setLink] = useState<LinkData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [showStoreLinks] = useState<boolean>(true);

    useEffect(() => {
        const fetchPostData = async (): Promise<void> => {
            try {
                setLoading(true);
                const response = await axios.get<LinkData>(`https://devapi.remspace.net/p/${postId}/${lang}`);
                setLink(response.data);
            } catch (error) {
                console.error('Error fetching post data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPostData();
    }, [postId, lang]);

    useEffect(() => {
        if (link) {
            const deepLink = `lucidme://posting-dream/${link.id}`;
            let fallbackTimeout: NodeJS.Timeout;

            if (isMobile()) {
                window.location.href = deepLink;
                fallbackTimeout = setTimeout(() => {}, 2500);

                const handleVisibilityChange = (): void => {
                    if (document.visibilityState === 'hidden') {
                        clearTimeout(fallbackTimeout);
                    }
                };

                document.addEventListener('visibilitychange', handleVisibilityChange);

                return () => {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                };
            }
        }
    }, [link]);

    if (loading) {
        return (
            <div className="loader-container">
                <BarLoader color="#00C4B4" width={500} />
            </div>
        );
    }

    if (!link) {
        return <div>Post not found</div>;
    }

    const shareUrl = `https://web.remspace.net/post/${link.id}`;
    const shareTitle = link.title;
    const shareDescription = link.post.dreams[0]?.dreamStory || '';
    const shareImage = link.fileUrl;

    return (
        <>
            <Helmet>
                <title>{shareTitle}</title>
                <meta name="description" content={shareDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:title" content={shareTitle} />
                <meta property="og:description" content={shareDescription} />
                <meta property="og:image" content={shareImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={shareUrl} />
                <meta name="twitter:title" content={shareTitle} />
                <meta name="twitter:description" content={shareDescription} />
                <meta name="twitter:image" content={shareImage} />
            </Helmet>
            <div className="app-container">
                <Header />
                <main className="main-content">
                    <div className="link-card">
                        <img
                            src={link.fileUrl}
                            className="link-image"
                            referrerPolicy="strict-origin-when-cross-origin"
                            alt={link.post.dreams[0]?.dreamTitle || 'Post image'}
                        />
                        <div className="link-text">
                            <PostInfo post={link.post} user={link.user} />
                            {link.post.dreams.map((dream, index) => (
                                <div key={index} className="dream-item">
                                    <h2>{dream.dreamTitle}</h2>
                                    <p className="description">{dream.dreamStory}</p>
                                </div>
                            ))}
                            <StoreLinks showStoreLinks={showStoreLinks} />
                            <ShareButtons shareUrl={shareUrl} shareTitle={shareTitle} />
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default App;
